// Salida del consultorio — «Descargar mis historias» (docs/cumplimiento/salida-consultorio.md).
//
// Es la ÚNICA pantalla que el administrador del consultorio ve durante el período de descarga que sigue a la baja por falta de pago: el servidor rechaza
// todo lo demás (bóveda: autorizarHceAdmin / lib/salida-consultorio-gate.ts). Descarga autenticada con el token del administrador (sin enlace reenviable).
// Texto pensado para personal NO técnico: lo técnico (huellas, formatos) va aparte, en «Para el equipo técnico».
import { AlertTriangle, CheckCircle2, Download, Loader2, LogOut } from 'lucide-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { cerrarSesionHceAdmin, leerSesionHceAdmin } from '@/auth/hceAdminSession';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { descargarPaqueteSalida, ErrorSalida, obtenerEstadoSalida, type EstadoSalida, type ResultadoDescargaSalida } from '@/lib/hceVault';

function fecha(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
}

function tamano(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function reloj(segundos: number): string {
  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
}

export function DescargarHistoriasPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sesion = leerSesionHceAdmin();
  const token = sesion?.token ?? null;

  const [estado, setEstado] = useState<EstadoSalida | null>(null);
  const [errorEstado, setErrorEstado] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [errorDescarga, setErrorDescarga] = useState<ErrorSalida | string | null>(null);
  const [resultado, setResultado] = useState<ResultadoDescargaSalida | null>(null);

  const cargar = useCallback(async () => {
    if (!token) return;
    try {
      setEstado(await obtenerEstadoSalida(token));
      setErrorEstado(null);
    } catch (err) {
      setErrorEstado(err instanceof ErrorSalida ? err.message : t('salida.errorEstado'));
    }
  }, [token, t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    if (!descargando) return undefined;
    setSegundos(0);
    const id = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [descargando]);

  async function descargar(aceptarIncompleta: boolean): Promise<void> {
    if (!token) return;
    setDescargando(true);
    setErrorDescarga(null);
    setResultado(null);
    try {
      setResultado(await descargarPaqueteSalida(token, { aceptarIncompleta }));
      await cargar();
    } catch (err) {
      setErrorDescarga(err instanceof ErrorSalida ? err : t('salida.errorDescarga'));
    } finally {
      setDescargando(false);
    }
  }

  function salir(): void {
    cerrarSesionHceAdmin();
    void navigate('/login');
  }

  if (!sesion) {
    return (
      <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4">
        <Alert variant="red" className="max-w-sm">
          <AlertDescription>{t('admin.usuarios.sesionVencida')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const abierto = estado?.enDescarga === true;
  const dias = estado?.diasRestantes ?? 0;
  const incompleta = errorDescarga instanceof ErrorSalida && errorDescarga.tipo === 'incompleta';
  const mensajeError = errorDescarga instanceof ErrorSalida ? (errorDescarga.tipo === 'incompleta' ? t('salida.errorIncompleta', { n: errorDescarga.omitidos }) : errorDescarga.message) : errorDescarga;

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg font-medium text-hce-ink">{t('salida.titulo')}</h1>
          <Button variant="outline" size="sm" onClick={salir}>
            <LogOut className="size-4" aria-hidden="true" /> {t('salida.cerrarSesion')}
          </Button>
        </div>

        {errorEstado && (
          <Alert variant="red">
            <AlertDescription>{errorEstado}</AlertDescription>
          </Alert>
        )}

        {estado && !abierto && (
          <Alert>
            <AlertTitle>{t('salida.sinPeriodoTitulo')}</AlertTitle>
            <AlertDescription>
              <p>{t('salida.sinPeriodo')}</p>
              <Button size="sm" className="mt-2" onClick={() => void navigate('/usuarios')}>
                {t('salida.irAUsuarios')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {abierto && estado && (
          <>
            <Alert variant="orange">
              <AlertTriangle aria-hidden="true" />
              <AlertTitle>{t('salida.avisoTitulo', { consultorio: estado.consultorio ?? '' })}</AlertTitle>
              <AlertDescription>
                <p>{t('salida.aviso', { fecha: fecha(estado.venceEl), dias })}</p>
              </AlertDescription>
            </Alert>

            <section className="rounded-md border border-border bg-card p-4 space-y-3">
              <h2 className="text-sm font-medium text-hce-ink">{t('salida.incluyeTitulo')}</h2>
              <ul className="list-disc pl-5 text-sm text-hce-muted space-y-1">
                <li>{t('salida.incluyeHistorias')}</li>
                <li>{t('salida.incluyeAdjuntos')}</li>
                <li>{t('salida.incluyeConsentimientos')}</li>
                <li>{t('salida.incluyeFacturacion')}</li>
                <li>{t('salida.incluyeManifiesto')}</li>
              </ul>
              <Button onClick={() => void descargar(false)} disabled={descargando}>
                {descargando ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
                {descargando ? t('salida.preparando') : t('salida.descargar')}
              </Button>
              {descargando && (
                <div aria-live="polite" className="space-y-1">
                  <progress className="h-1.5 w-full animate-pulse" aria-label={t('salida.preparando')} />
                  <p className="text-xs text-hce-muted">
                    {t('salida.preparandoDetalle')} {t('salida.transcurrido', { tiempo: reloj(segundos) })}
                  </p>
                </div>
              )}
            </section>

            {mensajeError && (
              <Alert variant={incompleta ? 'orange' : 'red'}>
                <AlertDescription>
                  <p>{mensajeError}</p>
                  {incompleta && (
                    <Button size="sm" variant="outline" className="mt-2" disabled={descargando} onClick={() => void descargar(true)}>
                      {t('salida.descargarIncompleta')}
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {resultado && (
              <section className="rounded-md border border-border bg-card p-4 space-y-2" aria-live="polite">
                <div className="flex items-center gap-2 text-sm font-medium text-hce-ink">
                  {resultado.completo ? <CheckCircle2 className="size-4 text-green-700" aria-hidden="true" /> : <AlertTriangle className="size-4 text-amber-600" aria-hidden="true" />}
                  {t('salida.resultadoTitulo')}
                </div>
                <p className="text-sm text-hce-muted">{t('salida.resultadoDetalle', { archivo: resultado.nombreArchivo, tamano: tamano(resultado.bytes) })}</p>
                {resultado.verificacion === 'no-verificable' && (
                  <Alert variant="orange">
                    <AlertDescription>{t('salida.huellaNoVerificable')}</AlertDescription>
                  </Alert>
                )}
                {!resultado.completo && (
                  <Alert variant="orange">
                    <AlertDescription>{t('salida.incompleta', { n: resultado.omitidos })}</AlertDescription>
                  </Alert>
                )}
                {resultado.completo && resultado.verificacion === 'si' && <p className="text-xs text-green-800">{t('salida.verificada')}</p>}
                <details className="text-xs text-hce-muted">
                  <summary className="cursor-pointer">{t('salida.tecnicoTitulo')}</summary>
                  <p className="mt-1 font-mono break-all">{t('salida.tecnicoHuella', { huella: resultado.sha256Navegador })}</p>
                  <p className="mt-1">{t('salida.tecnicoComoVerificar')}</p>
                </details>
              </section>
            )}

            {(estado.descargas ?? 0) > 0 && !resultado && (
              <p className="text-xs text-hce-muted">
                {t('salida.ultimaDescarga', { fecha: fecha(estado.ultimaDescargaEl), n: estado.descargas })}
                {estado.ultimaDescargaIncompleta ? ` ${t('salida.ultimaIncompleta')}` : ''}
              </p>
            )}

            <section className="text-xs text-hce-muted space-y-1">
              <p>{t('salida.comoVerificar')}</p>
              <p>{t('salida.responsable')}</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
